import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "../lib/brand";
import useSeo from "../lib/useSeo";

const Header = () => (
  <section className="border-b border-bos-ice" data-testid="services-header">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-24 pb-20 lg:pt-32 lg:pb-24">
      <p className="bos-eyebrow">Services</p>
      <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[64px] font-medium text-bos-navy tracking-tight leading-[1.08] max-w-4xl">
        Start where the question <span className="text-bos-primary">is clearest.</span>
      </h1>
      <p className="mt-8 max-w-2xl text-[17px] text-bos-charcoal/85 leading-[1.75]">
        Some people need a self-guided way to begin. Some need a second pair
        of eyes. Some need a full positioning foundation. The work should fit
        the season you are in.
      </p>
    </div>
  </section>
);

const ServiceCard = ({ s, index }) => (
  <article
    data-testid={`service-card-${s.id}`}
    className={`grid lg:grid-cols-[1fr_2fr] gap-10 py-12 lg:py-16 border-b border-bos-ice ${
      s.featured ? "bg-bos-ice/40 -mx-6 lg:-mx-10 px-6 lg:px-10" : ""
    }`}
  >
    <div>
      <div className="flex items-baseline gap-4">
        <span className="text-[11px] tracking-[0.18em] uppercase text-bos-mid">
          {String(index + 1).padStart(2, "0")} · {s.eyebrow}
        </span>
        {s.featured && (
          <span className="text-[10px] tracking-[0.16em] uppercase bg-bos-navy text-bos-white px-2.5 py-1 rounded-full">
            Flagship
          </span>
        )}
      </div>
      <h2 className="mt-4 text-[28px] lg:text-[34px] font-medium text-bos-navy tracking-tight leading-[1.15]">
        {s.name}
      </h2>
      <div className="mt-6 space-y-2 text-[13px] text-bos-charcoal/70">
        <div><span className="text-bos-charcoal/50">Duration · </span>{s.duration}</div>
        <div><span className="text-bos-charcoal/50">For · </span>{s.audience}</div>
        <div><span className="text-bos-charcoal/50">Investment · </span>{s.pricing}</div>
      </div>
    </div>
    <div>
      <p className="text-[16px] text-bos-charcoal/85 leading-[1.75] max-w-2xl">
        {s.summary}
      </p>
      <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3">
        {s.bullets.map((b) => (
          <li key={b} className="flex items-start gap-3 text-[14px] text-bos-charcoal/85">
            <span className="mt-2 inline-block h-1 w-1 rounded-full bg-bos-mid" />
            {b}
          </li>
        ))}
      </ul>
      {s.details && s.details.length > 0 && (
        <div className="mt-6 space-y-4 max-w-2xl" data-testid={`service-details-${s.id}`}>
          {s.details.map((d) => (
            <div key={d.label} className="border-l-2 border-bos-mid/40 pl-4">
              <div className="text-[11px] tracking-[0.18em] uppercase text-bos-mid">
                {d.label}
              </div>
              <p className="mt-1.5 text-[14px] text-bos-charcoal/80 leading-[1.7]">
                {d.body}
              </p>
            </div>
          ))}
        </div>
      )}
      <div className="mt-8 flex flex-wrap gap-5">
        <Link
          to={s.cta?.to || "/booking"}
          data-testid={`service-book-${s.id}`}
          className="inline-flex items-center gap-2 bg-bos-navy text-bos-white px-6 py-3 rounded-full hover:bg-bos-primary transition-colors text-[14px]"
        >
          {s.cta?.text || "Enquire about this"}
          <ArrowRight size={14} />
        </Link>
        {s.secondaryCta && (
          <Link
            to={s.secondaryCta.to}
            data-testid={`service-secondary-${s.id}`}
            className="inline-flex items-center gap-2 border border-bos-navy text-bos-navy px-6 py-3 rounded-full hover:bg-bos-navy hover:text-bos-white transition-colors text-[14px]"
          >
            {s.secondaryCta.text}
          </Link>
        )}
        <Link
          to="/contact"
          data-testid={`service-ask-${s.id}`}
          className="inline-flex items-center text-bos-primary bos-link-underline text-[14px]"
        >
          Ask a question first
        </Link>
      </div>
    </div>
  </article>
);

export default function Services() {
  useSeo({
    title: "Services · BuildOnce Studio",
    description:
      "Ways to work with BuildOnce Studio, from a self-guided Clarity Kit to a six-week Clarity Sprint and ongoing advisory. Singapore based.",
  });
  return (
    <div data-testid="services-page">
      <Header />
      <section className="mx-auto max-w-[1240px] px-6 lg:px-10">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.id} s={s} index={i} />
        ))}
      </section>

      <section className="border-b border-bos-ice bg-bos-ice/40" data-testid="services-case-study-banner">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-[2fr_1fr] gap-10 items-center">
          <div>
            <p className="bos-eyebrow">Curious how a sprint runs</p>
            <h2 className="mt-4 text-2xl lg:text-[36px] font-medium text-bos-navy tracking-tight leading-[1.15] max-w-2xl">
              Six-week walkthrough of the Clarity Sprint method.
            </h2>
          </div>
          <Link
            to="/case-studies/illustrative-clarity-sprint"
            data-testid="services-case-study-link"
            className="inline-flex items-center gap-2 bg-bos-navy text-bos-white px-6 py-3 rounded-full hover:bg-bos-primary transition-colors self-start"
          >
            See how it unfolds
          </Link>
        </div>
      </section>

      <section className="bg-bos-navy" data-testid="services-cta">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-[2fr_1fr] gap-10 items-end">
          <div>
            <p className="text-[12px] tracking-[0.18em] uppercase text-bos-light">Not sure which rung</p>
            <h2 className="mt-4 text-2xl lg:text-[40px] font-medium text-bos-white tracking-tight leading-[1.15] max-w-2xl">
              A short call is usually the fastest way to figure out where you
              actually need to start.
            </h2>
          </div>
          <Link
            to="/booking"
            data-testid="services-cta-book"
            className="inline-flex items-center bg-bos-white text-bos-navy px-7 py-3.5 rounded-full hover:bg-bos-ice transition-colors self-start"
          >
            Book a discovery call
          </Link>
        </div>
      </section>
    </div>
  );
}
